import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sparkles, X, ListChecks, CalendarClock, Plus, Check, Loader2, UserCheck } from 'lucide-react';
import api, { formatDate, formatDateTime } from '../utils/api';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  high: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
};

const ActionButton = ({ itemKey, onClick, label, creating, isCreated }) => isCreated ? (
  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 shrink-0">
    <Check className="w-3 h-3 mr-1" /> Created
  </Badge>
) : (
  <Button
    size="sm"
    className="h-7 text-xs shrink-0 bg-violet-600 hover:bg-violet-700 text-white"
    onClick={onClick}
    disabled={creating === itemKey}
    data-testid={`ai-create-${itemKey}`}
  >
    {creating === itemKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" />{label}</>}
  </Button>
);

/** Phase 35 — one-click "Create Action Item / Customer Follow-Up" chips
 *  generated by AI from a Discussion comment. */
const AIActionSuggestions = ({ leadId, data, onDismiss, onCreated }) => {
  const [creating, setCreating] = useState(null);
  const [created, setCreated] = useState({});

  if (!data) return null;
  const tasks = data.tasks || [];
  const followUps = data.follow_ups || [];
  if (tasks.length === 0 && followUps.length === 0) return null;

  const createTask = async (t, key) => {
    setCreating(key);
    try {
      await api.post('/tasks', {
        title: t.title,
        description: t.description || '',
        priority: t.priority || 'medium',
        due_date: t.due_date,
        lead_id: leadId,
      });
      setCreated((prev) => ({ ...prev, [key]: true }));
      toast.success(`Action item created: ${t.title}`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create action item');
    } finally {
      setCreating(null);
    }
  };

  const createFollowUp = async (f, key) => {
    setCreating(key);
    try {
      await api.post(`/leads/${leadId}/follow-ups`, {
        scheduled_date: f.scheduled_date,
        notes: f.notes,
        pending_with: f.pending_with || null,
        reminder_minutes_before: 120,
      });
      setCreated((prev) => ({ ...prev, [key]: true }));
      toast.success('Customer follow-up scheduled');
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to schedule follow-up');
    } finally {
      setCreating(null);
    }
  };

  return (
    <div
      className="rounded-lg border border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 p-3 space-y-2.5"
      data-testid="ai-action-suggestions"
    >
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
          AI suggested actions
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto text-muted-foreground hover:text-foreground"
          data-testid="dismiss-ai-suggestions"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {tasks.map((t, i) => {
        const key = `task-${i}`;
        return (
          <div key={key} className="flex items-start gap-2 p-2 rounded-md bg-white/70 dark:bg-card/70 border border-violet-100 dark:border-violet-900/50">
            <ListChecks className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium">{t.title}</span>
                <Badge className={`text-[10px] ${PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.medium}`}>{t.priority}</Badge>
                {t.due_date && <span className="text-[11px] text-muted-foreground">Due {formatDate(t.due_date)}</span>}
              </div>
              {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
            </div>
            <ActionButton itemKey={key} onClick={() => createTask(t, key)} label="Action Item" creating={creating} isCreated={!!created[key]} />
          </div>
        );
      })}

      {followUps.map((f, i) => {
        const key = `followup-${i}`;
        const hasTime = (f.scheduled_date || '').includes('T');
        return (
          <div key={key} className="flex items-start gap-2 p-2 rounded-md bg-white/70 dark:bg-card/70 border border-violet-100 dark:border-violet-900/50">
            <CalendarClock className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium">{f.notes}</span>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground mt-0.5">
                <span>{hasTime ? formatDateTime(f.scheduled_date) : formatDate(f.scheduled_date)}</span>
                {f.pending_with && (
                  <span className="inline-flex items-center gap-0.5 text-blue-600">
                    <UserCheck className="w-3 h-3" />
                    {f.pending_with === 'customer' ? 'Customer' : 'Selling Partner'}
                  </span>
                )}
              </div>
            </div>
            <ActionButton itemKey={key} onClick={() => createFollowUp(f, key)} label="Follow-Up" creating={creating} isCreated={!!created[key]} />
          </div>
        );
      })}
    </div>
  );
};

export default AIActionSuggestions;
